import { useMatch, useParams } from 'react-router-dom'

import { NotFound } from '@factorialco/deprecated-design-system'
import {
  ResourceFieldDataFragment,
  TasksValueObjectsTaskIdentifierEnum,
  ValueDataFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { TaskIdentifier } from 'modules/tasks/lib/task'

import { TaskElementContent } from './TaskElementContent'
import { useTaskDetails } from './useTaskDetail'

interface Props {
  taskId: string
  back?: string
  identifier?: TasksValueObjectsTaskIdentifierEnum
  canEmployeeManageAnyEmployeeFolder: boolean
  loading: boolean
  customResourceFieldsData: ResourceFieldDataFragment[]
  customValuesData: ValueDataFragment[]
}

const TaskElement = ({
  taskId,
  identifier,
  back,
  canEmployeeManageAnyEmployeeFolder,
  loading: parentLoading,
  customResourceFieldsData,
  customValuesData,
}: Props) => {
  const { company } = useCurrentSession()
  const { id: employeeId } = useParams()
  const { navigation } = useNavigation()
  const fromEployeePage = !!useMatch({
    path: employeeId
      ? navigation.employees.id.tasks.taskId.fullPath({ id: employeeId, taskId: String(taskId) })
      : '',
  })

  // Only single task can be seen from employee page, single tasks are task assigned to employee or created by employee
  const taskIdentifier = identifier || (fromEployeePage ? TaskIdentifier.Single : undefined)

  const {
    task,
    loading,
    canSeePersonalInfo,
    error,
    canEditPersonalInfo,
    hasDevTimeTrackingWorkableDaysDefinition,
    hasTimeTrackingAnnualisationDailyValue,
    referenceContract,
    jobCatalogContextlessPolicies,
    hasPayrollBundle,
    contractContextlessPolicies,
    customFieldsContextlessPolicies,
    jobCatalogTreeFeatureEnabled,
    jobCatalogTrees,
    hasProjectsTasksTimeTracking,
    canManageProjectTask,
  } = useTaskDetails({
    ids: [taskId],
    identifier: taskIdentifier,
    companyId: company?.id,
  })

  if (!loading && !task)
    return (
      <NotFound
        title={i18n.t('errors.page_not_found.title')}
        description={i18n.t('errors.page_not_found.description')}
      />
    )

  const authorized = !!task

  const backLink = {
    href: back || navigation.tasks.fullPath(),
    label: '',
  }

  return (
    <ResponsivePage.Body
      variant="fullscreen"
      loading={loading || parentLoading}
      error={error}
      showHeader={false}
      authorized={authorized}
      backLink={backLink}
    >
      {task && (
        <TaskElementContent
          task={task}
          canSeePersonalInfo={canSeePersonalInfo}
          canEditPersonalInfo={canEditPersonalInfo}
          canEmployeeManageAnyEmployeeFolder={canEmployeeManageAnyEmployeeFolder}
          customResourceFieldsData={customResourceFieldsData}
          customValuesData={customValuesData}
          hasDevTimeTrackingWorkableDaysDefinition={hasDevTimeTrackingWorkableDaysDefinition}
          hasTimeTrackingAnnualisationDailyValue={hasTimeTrackingAnnualisationDailyValue}
          referenceContract={referenceContract}
          jobCatalogContextlessPolicies={jobCatalogContextlessPolicies}
          hasPayrollBundle={hasPayrollBundle}
          contractContextlessPolicies={contractContextlessPolicies}
          customFieldsContextlessPolicies={customFieldsContextlessPolicies}
          jobCatalogTreeFeatureEnabled={jobCatalogTreeFeatureEnabled}
          jobCatalogTrees={jobCatalogTrees}
          hasProjectsTasksTimeTracking={hasProjectsTasksTimeTracking}
          canManageProjectTask={canManageProjectTask}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default observer(TaskElement)
